import React from "react";
import call from "./call";
import useQuery, { useGetQueryData, useMutateBody } from "./useQuery";
import type { Failure, Setup } from "./useSetups";
import { useSetups } from "./useSetups";
import { useChannelUpdates } from "./ws/useChannelUpdates";
import { getTimeSetup } from "diagnostics-domain/setup/getSetup";
export interface FailureValue {
  date: number;
  failureName: string;
  confidence: number;
  value: number;
  impacts: Record<string /* quantityId */, number /* value */>;
}

const PATH = "/projects/:projectId(uuid)/failure-values";

export const useFailureValues = (projectId: string) => {
  const getCachedItems = useGetQueryData(PATH, { routeParams: { projectId } });

  const fetchUpdatedItems = React.useCallback(
    async (since: string) => {
      const { failureValues, deletedFailureValueIds } = (
        await call("GET", "/projects/:projectId(uuid)/failure-values/updates/:since(date)", {
          routeParams: { projectId, since: new Date(since) },
        })
      ).body;
      return [failureValues, deletedFailureValueIds] as const;
    },
    [projectId],
  );

  useChannelUpdates(
    { key: "failure_values", projectId },
    {
      readItems: React.useCallback(() => getCachedItems()?.body, [getCachedItems]),
      fetchUpdatedItems,
      mutateItems: useMutateBody(PATH, { routeParams: { projectId } }),
    },
  );

  return useQuery(PATH, { routeParams: { projectId } }).body;
};

export type RawFailureValues = ReturnType<typeof useFailureValues>;

export function computeImpacts(failure: Failure, value: number) {
  return failure.impacts.reduce<{ [quantityId: string]: number }>((acc, impact) => {
    acc[impact.quantityId] = (impact.value * value) / failure.amplitude;
    return acc;
  }, {});
}

export type IndexedFailureValues = {
  [date: number]: {
    [failureName: string]: FailureValue;
  };
};

const cache = new WeakMap<RawFailureValues, WeakMap<Setup[], IndexedFailureValues>>();
export const useIndexedFailureValues = (projectId: string) => {
  const setups = useSetups(projectId);
  const rawFailureValues = useFailureValues(projectId);

  return React.useMemo(() => {
    let setupCache = cache.get(rawFailureValues);
    if (setupCache) {
      const cached = setupCache.get(setups);
      if (cached) return cached;
    }

    const dateIndexedFailureValues = rawFailureValues.reduce<{
      [date: number]: typeof rawFailureValues;
    }>((acc, failureValue) => {
      const analysisDate = failureValue.date;
      if (acc[analysisDate] === undefined) acc[analysisDate] = [];
      acc[analysisDate].push(failureValue);
      return acc;
    }, {});

    const failureBySetupIdAndName = setups.reduce<{
      [setupId: string]: { [failureName: string]: Failure };
    }>((acc, setup) => {
      acc[setup.id] = setup.failures.reduce<{ [failureName: string]: Failure }>((acc, failure) => {
        acc[failure.name] = failure;
        return acc;
      }, {});

      return acc;
    }, {});

    const result: { [date: number]: { [failureName: string]: FailureValue } } = {};

    for (const dateStr in dateIndexedFailureValues) {
      const date = Number(dateStr);
      const failureValues = dateIndexedFailureValues[date];
      const setup = getTimeSetup(setups, date);
      if (setup === null) continue;
      if (failureValues === undefined) throw new Error("Unexpected undefined failureValues");

      const indexedFailureValues: { [failureName: string]: FailureValue } = {};
      for (const fv of failureValues) {
        const failure = failureBySetupIdAndName[setup.id]?.[fv.failureName];
        if (failure === undefined) continue;
        indexedFailureValues[fv.failureName] = {
          date,
          failureName: fv.failureName,
          confidence: fv.confidence,
          value: fv.real,
          impacts: computeImpacts(failure, fv.real),
        };
      }

      result[date] = indexedFailureValues;
    }

    if (!setupCache) {
      setupCache = new WeakMap();
      cache.set(rawFailureValues, setupCache);
    }
    setupCache.set(setups, result);
    return result;
  }, [rawFailureValues, setups]);
};
